import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth, api } from '../contexts/AuthContext';
import { formatPHP } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { AlertTriangle, Download, RefreshCw, ArrowRightLeft, Filter, Calendar, MessageSquare, Send, Bell } from 'lucide-react';
import { toast } from 'sonner';

const BUCKETS = [
  { key: '30',    label: '30 Days',    desc: 'No sale 30–59 days' },
  { key: '60',    label: '60 Days',    desc: 'No sale 60–89 days' },
  { key: '90',    label: '90+ Days',   desc: 'Stale ≥ 90 days' },
  { key: 'never', label: 'Never Sold', desc: 'Stocked but never sold once' },
  { key: 'custom', label: 'Custom',    desc: 'Pick a custom window' },
];

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
  } catch { return '—'; }
}

function csvEscape(v) {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export default function SlowMovingStockPage() {
  const { user, currentBranch, branches = [] } = useAuth();
  const isAdmin = ['admin', 'owner'].includes((user?.role || '').toLowerCase());

  const [bucket, setBucket] = useState('90');
  const [branchId, setBranchId] = useState(isAdmin ? 'all' : (currentBranch?.id || user?.branch_id || ''));

  // Sync branchId when the user switches branch in the nav (non-admins always follow currentBranch)
  useEffect(() => {
    if (!isAdmin) {
      setBranchId(currentBranch?.id || user?.branch_id || '');
    }
  }, [currentBranch?.id, isAdmin, user?.branch_id]);
  const [customMode, setCustomMode] = useState('days'); // 'days' | 'range'
  const [customDays, setCustomDays] = useState(45);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ counts: {}, capital_by_bucket: {} });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  // Branch velocity modal
  const [velocityProduct, setVelocityProduct] = useState(null);
  const [velocityRows, setVelocityRows] = useState([]);
  const [velocityLoading, setVelocityLoading] = useState(false);
  const [velocityDays, setVelocityDays] = useState(30);

  // SMS toggle
  const [smsToggle, setSmsToggle] = useState({ enabled: true, threshold_days: 90 });
  const [smsBusy, setSmsBusy] = useState(false);

  // On-demand send state
  const [sendBusy, setSendBusy] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { telegram, sms, total_items, branch_name }

  const fetchToggle = useCallback(async () => {
    try {
      const res = await api.get('/inventory-reports/sms-toggles');
      setSmsToggle({
        enabled: res.data.po_slow_moving_alert,
        threshold_days: res.data.threshold_days,
      });
    } catch (e) {
      // non-blocking
    }
  }, []);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { bucket, branch_id: branchId || 'all' };
      if (bucket === 'custom') {
        if (customMode === 'days') {
          params.days = Number(customDays) || 0;
        } else {
          if (dateFrom) params.date_from = new Date(dateFrom).toISOString();
          if (dateTo) {
            const dt = new Date(dateTo);
            dt.setHours(23, 59, 59, 999);
            params.date_to = dt.toISOString();
          }
        }
      }
      const res = await api.get('/inventory-reports/slow-moving', { params });
      setRows(res.data.rows || []);
      setSummary(res.data.summary || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load slow-moving report');
    } finally {
      setLoading(false);
    }
  }, [bucket, branchId, customMode, customDays, dateFrom, dateTo]);

  useEffect(() => { fetchToggle(); }, [fetchToggle]);
  useEffect(() => { fetchRows(); }, [fetchRows]);

  const filteredRows = useMemo(() => {
    if (!search) return rows;
    const q = search.trim().toLowerCase();
    return rows.filter(r =>
      (r.product_name || '').toLowerCase().includes(q) ||
      (r.sku || '').toLowerCase().includes(q) ||
      (r.barcode || '').toLowerCase().includes(q) ||
      (r.branch_name || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const totalCapital = useMemo(
    () => filteredRows.reduce((s, r) => s + (r.capital_value || 0), 0),
    [filteredRows]
  );

  const onExportCSV = () => {
    if (!filteredRows.length) {
      toast.warning('Nothing to export');
      return;
    }
    const headers = [
      'SKU', 'Product', 'Branch', 'Stock', 'Unit Cost', 'Capital Value',
      'Unit Price', 'Retail Value', 'Last Sale Date', 'Days Since Last Sale', 'Bucket',
    ];
    const lines = [headers.join(',')];
    for (const r of filteredRows) {
      lines.push([
        csvEscape(r.sku),
        csvEscape(r.product_name),
        csvEscape(r.branch_name),
        r.current_qty,
        r.unit_cost,
        r.capital_value,
        r.unit_price,
        r.retail_value,
        r.last_sale_date ? fmtDate(r.last_sale_date) : 'Never',
        r.days_since_last_sale ?? '—',
        r.bucket === '30' ? '30 Days'
          : r.bucket === '60' ? '60 Days'
          : r.bucket === '90' ? '90+ Days'
          : r.bucket === 'never' ? 'Never Sold' : 'Fresh',
      ].map(csvEscape).join(','));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const stamp = new Date().toISOString().slice(0, 10);
    a.download = `slow-moving-stock-${bucket}-${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openVelocity = async (row) => {
    setVelocityProduct(row);
    setVelocityRows([]);
    setVelocityLoading(true);
    try {
      const res = await api.get(`/inventory-reports/branch-velocity/${row.product_id}`, {
        params: { days: velocityDays },
      });
      setVelocityRows(res.data.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load branch velocity');
    } finally {
      setVelocityLoading(false);
    }
  };

  const refreshVelocity = async (days) => {
    if (!velocityProduct) return;
    setVelocityDays(days);
    setVelocityLoading(true);
    try {
      const res = await api.get(`/inventory-reports/branch-velocity/${velocityProduct.product_id}`, {
        params: { days },
      });
      setVelocityRows(res.data.rows || []);
    } catch (e) {
      toast.error('Failed to load velocity');
    } finally {
      setVelocityLoading(false);
    }
  };

  const saveToggle = async (next) => {
    setSmsBusy(true);
    try {
      const res = await api.post('/inventory-reports/sms-toggles', next);
      setSmsToggle({
        enabled: res.data.po_slow_moving_alert,
        threshold_days: res.data.threshold_days,
      });
      toast.success('SMS alert settings saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSmsBusy(false);
    }
  };

  const sendNow = async (channels) => {
    setSendBusy(true);
    setSendResult(null);
    try {
      const res = await api.post('/inventory-reports/send-slow-moving-alert', {
        branch_id: branchId || 'all',
        threshold_days: smsToggle.threshold_days || 30,
        channels,
      });
      setSendResult(res.data);
      const tg = res.data.telegram || 0;
      const sms = res.data.sms || 0;
      const total = res.data.total_items || 0;
      if (!total) {
        toast.info(`No slow-moving items found for this threshold.`);
      } else {
        toast.success(
          `Sent ${total} item(s): ${tg} Telegram chat(s)${sms ? `, ${sms} SMS` : ''}`
        );
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally {
      setSendBusy(false);
    }
  };

  // Bucket counts from summary
  const bucketCount = (key) => summary.counts?.[key] ?? 0;

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="slow-moving-stock-page">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight flex items-center gap-2">
            <AlertTriangle className="h-7 w-7 text-amber-600" />
            Slow-Moving Stock
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Products with stock on hand that haven&apos;t been <strong>sold</strong> within the selected window.
            Restocks (PO, branch transfer) do not reset the clock — only customer sales do.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading} data-testid="slow-moving-refresh-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={onExportCSV} disabled={!filteredRows.length} data-testid="slow-moving-export-btn">
            <Download className="h-4 w-4 mr-1.5" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {['30', '60', '90', 'never'].map((k) => {
          const labels = { '30': '30 Days', '60': '60 Days', '90': '90+ Days', 'never': 'Never Sold' };
          const tones = {
            '30': 'border-yellow-300 bg-yellow-50',
            '60': 'border-orange-300 bg-orange-50',
            '90': 'border-red-300 bg-red-50',
            'never': 'border-zinc-400 bg-zinc-100',
          };
          return (
            <Card key={k} className={`border ${tones[k]}`} data-testid={`slow-moving-summary-${k}`}>
              <CardContent className="p-4">
                <div className="text-xs font-semibold text-zinc-600 uppercase tracking-wide">{labels[k]}</div>
                <div className="text-2xl font-bold mt-1">{bucketCount(k)}</div>
                <div className="text-xs text-zinc-500 mt-1">
                  Capital tied up:{' '}
                  <span className="font-semibold text-zinc-800">
                    {formatPHP(summary.capital_by_bucket?.[k] || 0)}
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ── Filters ────────────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            {/* RB-INV-02 (BT Batch 2 P5): the monthly report used to exist
                only on Telegram — now downloadable here for any org. */}
            <Button
              variant="outline"
              className="h-10 order-last"
              data-testid="download-monthly-report-btn"
              onClick={async () => {
                try {
                  const params = branchId && branchId !== 'all' ? { branch_id: branchId } : {};
                  const res = await api.get('/inventory-reports/monthly-report/pdf', {
                    params, responseType: 'blob',
                  });
                  const url = window.URL.createObjectURL(res.data);
                  const a = document.createElement('a');
                  a.href = url;
                  const cd = res.headers['content-disposition'] || '';
                  const m = cd.match(/filename="?([^";]+)"?/);
                  a.download = m ? m[1] : 'Slow-Moving Monthly Report.pdf';
                  a.click();
                  window.URL.revokeObjectURL(url);
                } catch { /* toast below */
                  const { toast } = await import('sonner');
                  toast.error('Could not generate the monthly report');
                }
              }}
            >
              Download Monthly Report (PDF)
            </Button>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs text-zinc-600 mb-1 block">Search</Label>
              <Input
                placeholder="Product, SKU, barcode, branch…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="slow-moving-search-input"
              />
            </div>
            {isAdmin && (
              <div className="min-w-[200px]">
                <Label className="text-xs text-zinc-600 mb-1 block">Branch</Label>
                <Select value={branchId} onValueChange={setBranchId}>
                  <SelectTrigger data-testid="slow-moving-branch-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!isAdmin && (
              <div className="text-sm text-zinc-600 pb-2">
                <Badge variant="secondary">{currentBranch?.name || 'Your branch'}</Badge>
              </div>
            )}
          </div>

          {/* Bucket tabs */}
          <Tabs value={bucket} onValueChange={setBucket} className="w-full">
            <TabsList className="flex flex-wrap h-auto gap-1 bg-zinc-100 p-1">
              {BUCKETS.map((b) => (
                <TabsTrigger
                  key={b.key}
                  value={b.key}
                  className="data-[state=active]:bg-white"
                  data-testid={`slow-moving-tab-${b.key}`}
                >
                  {b.label}
                  {b.key !== 'custom' && (
                    <Badge variant="outline" className="ml-2 px-1.5 py-0 text-[10px]">
                      {bucketCount(b.key)}
                    </Badge>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* Custom mode controls */}
          {bucket === 'custom' && (
            <div className="rounded-md border border-zinc-200 p-3 space-y-3 bg-zinc-50">
              <div className="flex items-center gap-3">
                <Filter className="h-4 w-4 text-zinc-500" />
                <span className="text-sm font-medium">Custom Filter Mode</span>
                <div className="ml-auto flex gap-1 bg-white rounded-md border border-zinc-200 p-0.5">
                  <button
                    type="button"
                    onClick={() => setCustomMode('days')}
                    className={`text-xs px-3 py-1 rounded ${customMode === 'days' ? 'bg-zinc-900 text-white' : 'text-zinc-600'}`}
                    data-testid="slow-moving-custom-mode-days"
                  >
                    Days threshold
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomMode('range')}
                    className={`text-xs px-3 py-1 rounded ${customMode === 'range' ? 'bg-zinc-900 text-white' : 'text-zinc-600'}`}
                    data-testid="slow-moving-custom-mode-range"
                  >
                    Date range
                  </button>
                </div>
              </div>
              {customMode === 'days' ? (
                <div className="flex items-end gap-2">
                  <div className="w-40">
                    <Label className="text-xs text-zinc-600 mb-1 block">Days since last sale ≥</Label>
                    <Input
                      type="number"
                      min={1}
                      value={customDays}
                      onChange={(e) => setCustomDays(e.target.value)}
                      data-testid="slow-moving-custom-days-input"
                    />
                  </div>
                  <Button onClick={fetchRows} variant="secondary" size="sm">Apply</Button>
                </div>
              ) : (
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="text-xs text-zinc-600 mb-1 block flex items-center gap-1">
                      <Calendar className="h-3 w-3" /> Last sale from
                    </Label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      data-testid="slow-moving-custom-from-input"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-600 mb-1 block">To</Label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      data-testid="slow-moving-custom-to-input"
                    />
                  </div>
                  <Button onClick={fetchRows} variant="secondary" size="sm">Apply</Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Notifications Panel (admin only) ───────────────────────────────── */}
      {isAdmin && (
        <Card className="border-amber-200 bg-amber-50/40">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-amber-700" />
              <div className="flex-1">
                <div className="text-sm font-semibold">Slow-Moving Stock Notifications</div>
                <div className="text-xs text-zinc-500">
                  Branch-specific · Full product list · {branchId === 'all' ? 'All Branches' : (branches.find(b=>b.id===branchId)?.name || 'Selected Branch')}
                </div>
              </div>
            </div>

            {/* Threshold + scheduled toggle */}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <div className="flex items-center gap-2">
                <Label className="text-xs text-zinc-600 whitespace-nowrap">Threshold days</Label>
                <Input
                  type="number" min={7}
                  value={smsToggle.threshold_days}
                  onChange={(e) => setSmsToggle(s => ({ ...s, threshold_days: Number(e.target.value) || 30 }))}
                  className="w-20 h-8"
                  data-testid="slow-moving-sms-threshold-input"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={smsToggle.enabled}
                  onCheckedChange={(v) => saveToggle({ enabled: v, threshold_days: smsToggle.threshold_days })}
                  disabled={smsBusy}
                  data-testid="slow-moving-sms-toggle"
                />
                <Label className="text-xs text-zinc-600">Auto-SMS on PO reorder</Label>
              </div>
              <Button size="sm" variant="outline" onClick={() => saveToggle(smsToggle)} disabled={smsBusy} data-testid="slow-moving-sms-save-btn">
                Save schedule
              </Button>
            </div>

            {/* Send Now buttons */}
            <div className="flex flex-wrap gap-2 pt-1 border-t border-amber-200">
              <span className="text-xs text-zinc-500 self-center">Send now:</span>
              <Button
                size="sm" variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={() => sendNow(['telegram'])}
                disabled={sendBusy}
                data-testid="slow-moving-send-telegram-btn"
              >
                <Send size={13} className="mr-1.5" />
                {sendBusy ? 'Sending…' : 'Telegram (full list)'}
              </Button>
              <Button
                size="sm" variant="outline"
                className="border-green-300 text-green-700 hover:bg-green-50"
                onClick={() => sendNow(['sms'])}
                disabled={sendBusy}
                data-testid="slow-moving-send-sms-btn"
              >
                <MessageSquare size={13} className="mr-1.5" />
                {sendBusy ? 'Sending…' : 'SMS (summary)'}
              </Button>
              <Button
                size="sm" variant="outline"
                className="border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => sendNow(['telegram', 'sms'])}
                disabled={sendBusy}
                data-testid="slow-moving-send-both-btn"
              >
                <Send size={13} className="mr-1.5" />
                Both
              </Button>
            </div>

            {/* Last send result */}
            {sendResult && (
              <div className="text-xs text-zinc-500 bg-white border border-amber-100 rounded px-3 py-2" data-testid="slow-moving-send-result">
                Last send — <span className="font-semibold">{sendResult.branch_name}</span>:
                {' '}{sendResult.total_items} item(s) ·
                {' '}{sendResult.telegram} Telegram chat(s) ·
                {' '}{sendResult.sms} SMS
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── Results Table ──────────────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between text-sm">
            <div className="text-zinc-600">
              <span className="font-semibold text-zinc-900">{filteredRows.length}</span> product/branch row(s)
              {' · '}Capital tied up:{' '}
              <span className="font-semibold text-zinc-900">{formatPHP(totalCapital)}</span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Branch</TableHead>
                  <TableHead className="text-right">Stock</TableHead>
                  <TableHead className="text-right">Capital</TableHead>
                  <TableHead className="text-right">Retail</TableHead>
                  <TableHead>Last Sale</TableHead>
                  <TableHead className="text-center">Idle</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-zinc-500">Loading…</TableCell></TableRow>
                )}
                {!loading && !filteredRows.length && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-10 text-zinc-500">
                      No slow-moving stock in this view. 🎉
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredRows.map((r) => (
                  <TableRow key={`${r.product_id}-${r.branch_id}`} data-testid={`slow-moving-row-${r.product_id}`}>
                    <TableCell className="text-xs">{r.sku || '—'}</TableCell>
                    <TableCell className="font-medium">{r.product_name}</TableCell>
                    <TableCell>{r.branch_name}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.current_qty}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatPHP(r.capital_value)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatPHP(r.retail_value)}</TableCell>
                    <TableCell className="text-xs">
                      {r.last_sale_date ? fmtDate(r.last_sale_date) : <span className="text-zinc-400">Never</span>}
                    </TableCell>
                    <TableCell className="text-center">
                      {r.days_since_last_sale === null ? (
                        <Badge variant="outline" className="border-zinc-400 text-zinc-700">Never</Badge>
                      ) : r.days_since_last_sale >= 90 ? (
                        <Badge className="bg-red-100 text-red-800 hover:bg-red-100">{r.days_since_last_sale}d</Badge>
                      ) : r.days_since_last_sale >= 60 ? (
                        <Badge className="bg-orange-100 text-orange-800 hover:bg-orange-100">{r.days_since_last_sale}d</Badge>
                      ) : r.days_since_last_sale >= 30 ? (
                        <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">{r.days_since_last_sale}d</Badge>
                      ) : (
                        <Badge variant="outline">{r.days_since_last_sale}d</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openVelocity(r)}
                        data-testid={`slow-moving-suggest-transfer-${r.product_id}`}
                      >
                        <ArrowRightLeft className="h-4 w-4 mr-1" />
                        Suggest Transfer
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* ── Branch Velocity Dialog ─────────────────────────────────────────── */}
      <Dialog open={!!velocityProduct} onOpenChange={(o) => !o && setVelocityProduct(null)}>
        <DialogContent className="max-w-2xl" data-testid="slow-moving-velocity-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-blue-600" />
              Branch Sales Velocity
            </DialogTitle>
            <DialogDescription>
              {velocityProduct ? (
                <>
                  <span className="font-semibold text-zinc-800">{velocityProduct.product_name}</span>
                  {velocityProduct.sku && <span className="ml-2 text-xs text-zinc-500">{velocityProduct.sku}</span>}
                </>
              ) : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-600">Window:</span>
            {[7, 30, 60, 90].map((d) => (
              <button
                key={d}
                type="button"
                className={`px-2 py-1 rounded border ${velocityDays === d ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white border-zinc-200 hover:bg-zinc-50'}`}
                onClick={() => refreshVelocity(d)}
                data-testid={`slow-moving-velocity-window-${d}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Branch</TableHead>
                <TableHead className="text-right">Units Sold ({velocityDays}d)</TableHead>
                <TableHead className="text-right">Transactions</TableHead>
                <TableHead className="text-right">Current Stock</TableHead>
                <TableHead>Last Sale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {velocityLoading && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-zinc-500">Loading…</TableCell></TableRow>
              )}
              {!velocityLoading && !velocityRows.length && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-zinc-500">No data for any branch.</TableCell></TableRow>
              )}
              {!velocityLoading && velocityRows.map((row, idx) => {
                const isTop = idx === 0 && (row.units_sold || 0) > 0;
                return (
                  <TableRow key={row.branch_id} className={isTop ? 'bg-green-50' : ''}>
                    <TableCell className="font-medium">
                      {row.branch_name}
                      {isTop && <Badge className="ml-2 bg-green-100 text-green-800 hover:bg-green-100">Top mover</Badge>}
                      {velocityProduct?.branch_id === row.branch_id && (
                        <Badge variant="outline" className="ml-2">Current</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{row.units_sold}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.transactions}</TableCell>
                    <TableCell className="text-right tabular-nums">{row.current_qty}</TableCell>
                    <TableCell className="text-xs">
                      {row.last_sale_date ? fmtDate(row.last_sale_date) : <span className="text-zinc-400">—</span>}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {velocityRows.length > 1 && velocityRows[0]?.units_sold > 0 && (
            <div className="rounded-md bg-blue-50 border border-blue-200 px-3 py-2 text-sm text-blue-900">
              <strong>Suggestion:</strong> Move stock from{' '}
              <strong>{velocityProduct?.branch_name}</strong> (idle{' '}
              {velocityProduct?.days_since_last_sale ?? '∞'}d) to{' '}
              <strong>{velocityRows[0].branch_name}</strong> — they sold{' '}
              <strong>{velocityRows[0].units_sold}</strong> unit(s) in the last {velocityDays} days.
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
